import { appAlert } from '@/lib/appAlert';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '@/lib/functions';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image as ExpoImage } from 'expo-image';
import { ActivityIndicator, Image, Pressable, ScrollView, Share, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { ChatBubble } from '@/components/ChatBubble';
import { ReviewSummaryCard } from '@/components/ReviewSummaryCard';
import { Avatar } from '@/components/ui/Avatar';
import { LevelBadge, type LevelInfo } from '@/components/ui/LevelBadge';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useAuth } from '@/contexts/AuthContext';
import { useNeedsAction } from '@/contexts/NeedsActionContext';
import { useTheme } from '@/contexts/ThemeContext';
import type { Profile, ReviewSummary } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';
import { fetchPublicProfilesByIds, listAgreements } from '@/lib/agreements';
import type { Agreement } from '@/lib/database.types';
import {
  getConnectionStatus,
  sendConnectionRequest,
  respondConnectionRequest,
  removeConnection,
  type ConnectionStatus,
} from '@/lib/connections';
import { getConnectionIdWith } from '@/lib/connectionMessages';
import { formatDate, statusLabel } from '@/lib/format';
import { getUserLevel } from '@/lib/levels';
import { fetchProfile } from '@/lib/profiles';
import { fetchMyReliabilityPercent, fetchReviewsFor, getReviewSummary, type ReviewWithStatus } from '@/lib/reviews';
import { fetchDisputesForProfile, type DisputeOutcomeEntry } from '@/lib/disputes';
import { recordVisit } from '@/lib/recent-users';
import { getPortfolio, type Portfolio } from '@/lib/portfolio';
import { getCircleAnnouncements, type Announcement } from '@/lib/announcements';
import { ErrorText } from '@/components/ErrorText';

export default function UserProfileScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { user } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { refresh: refreshBadge } = useNeedsAction();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [reviews, setReviews] = useState<ReviewWithStatus[]>([]);
  const [reviewers, setReviewers] = useState<Record<string, Profile>>({});
  const [reviewerReliability, setReviewerReliability] = useState<Record<string, number | null>>({});
  const [levelInfo, setLevelInfo] = useState<LevelInfo | null>(null);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [disputeOutcomes, setDisputeOutcomes] = useState<DisputeOutcomeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connStatus, setConnStatus] = useState<ConnectionStatus>('none');
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [connBusy, setConnBusy] = useState(false);
  const [commonAgreements, setCommonAgreements] = useState<Agreement[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const [p, s, r, lvl, pf, cs, disputes] = await Promise.all([
        fetchProfile(id),
        getReviewSummary(id),
        fetchReviewsFor(id),
        getUserLevel(id).catch(() => null),
        getPortfolio(id).catch(() => null),
        user && user.id !== id ? getConnectionStatus(id).catch(() => 'none' as ConnectionStatus) : Promise.resolve('none' as ConnectionStatus),
        fetchDisputesForProfile(id).catch(() => [] as DisputeOutcomeEntry[]),
      ]);
      setProfile(p);
      setSummary(s);
      setReviews(r);
      setLevelInfo(lvl);
      setPortfolio(pf);
      setConnStatus(cs);
      setConnectionId(cs === 'accepted' ? await getConnectionIdWith(id).catch(() => null) : null);
      setDisputeOutcomes(disputes);
      const reviewerProfiles = await fetchPublicProfilesByIds(r.map((x) => x.reviewer_id));
      setReviewers(reviewerProfiles);
      const reliabilityEntries = await Promise.all(
        Object.entries(reviewerProfiles).map(async ([rid, v]) => [
          rid,
          await fetchMyReliabilityPercent(rid, (v as any).phone_verified, !!(v as any).identity_verified_at).catch(() => null),
        ] as const)
      );
      setReviewerReliability(Object.fromEntries(reliabilityEntries));
      if (p && user?.id !== id) {
        void recordVisit({ id: p.id, name: p.full_name?.trim() || t('common.user'), avatarUrl: p.avatar_url ?? null });
        const anns = await getCircleAnnouncements().catch(() => []);
        const ann = anns.find((a) => a.creator_id === id) ?? null;
        setAnnouncement(ann);
        // Κοινές συμφωνίες
        if (user) {
          const { data: shared } = await supabase
            .from('agreements')
            .select('*')
            .or(`and(creator_id.eq.${user.id},counterparty_id.eq.${id}),and(creator_id.eq.${id},counterparty_id.eq.${user.id})`)
            .in('status', ['active', 'pending', 'completed'])
            .order('created_at', { ascending: false })
            .limit(5);
          setCommonAgreements(shared ?? []);
        }
      }
    } catch (e) {
      setError(getErrorMessage(e, t('common.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  async function onSendRequest() {
    if (!id) return;
    setConnBusy(true);
    try {
      await sendConnectionRequest(id);
      setConnStatus('pending_sent');
    } catch (e) {
      const msg = getErrorMessage(e, '');
      if (!msg.includes('cooldown') && !msg.includes('pending') && !msg.includes('connected')) {
        appAlert(t('common.error'), t('user.error_sendRequest'));
      }
    } finally {
      setConnBusy(false);
    }
  }

  async function onRespond(accept: boolean) {
    if (!id) return;
    setConnBusy(true);
    try {
      await respondConnectionRequest(id, accept);
      setConnStatus(accept ? 'accepted' : 'none');
      refreshBadge();
    } catch {
      appAlert(t('common.error'), t('user.error_response'));
    } finally {
      setConnBusy(false);
    }
  }

  async function onRemove() {
    if (!id) return;
    setConnBusy(true);
    try {
      await removeConnection(id);
      setConnStatus('none');
    } catch {
      appAlert(t('common.error'), t('user.error_remove'));
    } finally {
      setConnBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const name = profile?.full_name?.trim() || t('common.user');
  const isSelf = !!user && user.id === id;
  const inCircle = connStatus === 'accepted';
  const canSeePortfolio = !!portfolio?.enabled && (portfolio.public || isSelf || inCircle);

  if (profile?.deleted_at) {
    return (
      <View style={[styles.flex, styles.loader]}>
        <View style={styles.topBar}>
          <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button" style={{ position: 'absolute', top: insets.top + spacing.lg, left: spacing.lg }}>
            <Text style={styles.back}>{t('common.back')}</Text>
          </Pressable>
        </View>
        <Avatar uri={null} name="?" size={88} />
        <Text style={[styles.name, { color: colors.textMuted, marginTop: spacing.md }]}>{name}</Text>
        <Text style={[styles.bio, { marginTop: spacing.sm }]}>{t('user.deletedMember')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.flex}>
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        {profile && (
          <Pressable
            hitSlop={10}
            onPress={() => {
              const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';
              const profileUrl = `${supabaseUrl}/functions/v1/profile-page/${id}`;
              void Share.share({
                title: t('user.shareTitle', { name }),
                message: t('user.shareMessage', { name, url: profileUrl }),
                url: profileUrl,
              });
            }}
          >
            <Ionicons name="share-outline" size={20} color={colors.primaryLight} />
          </Pressable>
        )}
      </View>

      <View style={styles.header}>
        {/* Avatar με badges overlay στο κάτω μέρος */}
        <View style={styles.avatarWrap}>
          <Avatar
            uri={canSeePortfolio && profile?.use_logo_as_avatar && profile?.business_logo_url ? profile.business_logo_url : profile?.avatar_url}
            name={name}
            size={88}
          />
          {(!!profile?.verified || !!profile?.phone_verified || !!levelInfo || !!profile?.portfolio_enabled) && (
            <View style={styles.badgesOverlay}>
              {profile?.verified ? (
                <VerifiedBadge compact />
              ) : !!profile?.phone_verified && (
                <VerifiedBadge compact kind="phone" />
              )}
              {levelInfo && (
                <LevelBadge
                  info={levelInfo}
                  size="md"
                  memberSince={profile?.created_at ? formatDate(profile.created_at) : undefined}
                  avgRating={summary?.effective_rating ?? null}
                />
              )}
              {canSeePortfolio && <ProviderBadge compact />}
            </View>
          )}
        </View>

        {/* Όνομα */}
        <Text style={styles.name} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>
          {name}
        </Text>

        {/* Ψευδώνυμο/γνωστό ως */}
        {!!(profile as any)?.nickname?.trim() && (
          <Text style={styles.nickname} numberOfLines={1}>
            {t('user.knownAs', { nickname: (profile as any).nickname.trim() })}
          </Text>
        )}

        {/* Όνομα επιχείρησης */}
        {canSeePortfolio && !!profile?.show_business_name && !!profile?.business_name?.trim() && (
          <Text style={styles.businessName}>{profile.business_name.trim()}</Text>
        )}

        {/* Bio */}
        {!!profile?.bio?.trim() && (
          <Text style={styles.bio} numberOfLines={3}>{profile.bio}</Text>
        )}

        {!!(profile as any)?.dispute_win_rate_flagged && (
          <View style={styles.disputeFlagPill}>
            <Ionicons name="information-circle-outline" size={13} color={colors.textMuted} />
            <Text style={styles.disputeFlagText}>{t('user.unusualDisputeWinRate')}</Text>
          </View>
        )}

        {/* Μέλος από + ολοκληρωμένες */}
        <View style={styles.infoStrip}>
          {!!profile?.created_at && (
            <View style={styles.infoItem}>
              <Ionicons name="calendar-outline" size={13} color={colors.textMuted} />
              <Text style={styles.infoText} numberOfLines={1}>{t('user.memberSince')} {formatDate(profile.created_at)}</Text>
            </View>
          )}
          {!!levelInfo && levelInfo.completed > 0 && (
            <View style={styles.infoItem}>
              <Ionicons name="checkmark-circle-outline" size={13} color={colors.textMuted} />
              <Text style={styles.infoText} numberOfLines={1}>{levelInfo.completed} {t('user.completed')}</Text>
            </View>
          )}
          {!!summary?.avg_rating && summary.review_count > 0 && (
            <View style={styles.infoItem}>
              <Ionicons name="star-outline" size={13} color={colors.textMuted} />
              <Text style={styles.infoText} numberOfLines={1}>{summary.avg_rating.toFixed(1)} ({summary.review_count})</Text>
            </View>
          )}
        </View>
      </View>

      {!!announcement && (
        <AnnouncementCard announcement={announcement} colors={colors} styles={styles} />
      )}

      {summary && (
        <ReviewSummaryCard
          summary={summary}
          reviews={reviews}
          reviewerNames={Object.fromEntries(
            Object.entries(reviewers).map(([k, v]) => [k, v.full_name?.trim() || t('common.user')])
          )}
          reviewerNicknames={Object.fromEntries(
            Object.entries(reviewers).map(([k, v]) => [k, (v as any).nickname ?? null])
          )}
          reviewerReliability={reviewerReliability}
          disputeOutcomes={disputeOutcomes}
        />
      )}

      {!isSelf && profile && (
        <ConnectionBlock
          status={connStatus}
          busy={connBusy}
          onSend={onSendRequest}
          onAccept={() => onRespond(true)}
          onDecline={() => onRespond(false)}
          onRemove={onRemove}
        />
      )}

      {!isSelf && profile && (
        <Button
          title={t('home.action.newAgreement')}
          icon="add"
          onPress={() =>
            router.push({
              pathname: '/(app)/agreements/new',
              params: { counterpartyId: id, counterpartyName: name },
            })
          }
        />
      )}

      {/* Κοινές ενεργές συμφωνίες */}
      {!isSelf && commonAgreements.length > 0 && (
        <View style={styles.portfolioCard}>
          <View style={styles.portfolioHeader}>
            <View style={styles.portfolioTitleRow}>
              <Ionicons name="document-text-outline" size={16} color={colors.primary} />
              <Text style={styles.portfolioTitle}>{t('user.commonAgreements')}</Text>
            </View>
          </View>
          {commonAgreements.map((a) => (
            <Pressable
              key={a.id}
              style={styles.agreementCard}
              onPress={() => router.push({ pathname: '/(app)/agreements/[id]', params: { id: a.id } })}
              accessibilityRole="button"
            >
              <View style={styles.agreementMain}>
                <Ionicons name="document-outline" size={16} color={colors.primary} style={{ marginTop: 1 }} />
                <Text style={styles.agreementTitle} numberOfLines={1}>{a.title}</Text>
              </View>
              <View style={styles.agreementMeta}>
                <Text style={styles.agreementDate}>{formatDate(a.created_at)}</Text>
                <Text style={[styles.agreementDate, { color: colors.primary }]}>{statusLabel(a.status)}</Text>
              </View>
            </Pressable>
          ))}
        </View>
      )}

      {canSeePortfolio && portfolio && (
        <PortfolioSection portfolio={portfolio} profile={profile} styles={styles} colors={colors} />
      )}

      <ErrorText>{error}</ErrorText>
    </ScrollView>

    {!isSelf && inCircle && <ChatBubble otherUserId={id} label={name} connectionId={connectionId ?? undefined} />}
    </View>
  );
}

// ─── PortfolioSection ─────────────────────────────────────────────────────────

type PortfolioSectionProps = {
  portfolio: Portfolio;
  profile: Profile | null;
  styles: ReturnType<typeof makeStyles>;
  colors: AppColors;
};

function PortfolioSection({ portfolio, profile, styles, colors }: PortfolioSectionProps) {
  const { t } = useTranslation();
  return (
    <View style={styles.portfolioCard}>
      {/* Τίτλος section */}
      <View style={styles.portfolioHeader}>
        <View style={styles.portfolioTitleRow}>
          <Ionicons name="briefcase-outline" size={16} color={colors.primary} />
          <Text style={styles.portfolioTitle}>{t('user.professionalProfile')}</Text>
        </View>
        <ProviderBadge compact />
      </View>

      {/* Business logo + specialty */}
      {(!!profile?.business_logo_url || !!portfolio.specialty) && (
        <View style={styles.businessLogoSection}>
          {!!profile?.business_logo_url && (
            <Image
              source={{ uri: profile.business_logo_url }}
              style={styles.businessLogo}
              resizeMode="contain"
            />
          )}
          {!!portfolio.specialty && (
            <View style={styles.specialtyBadge}>
              <Text style={styles.specialtyText}>{portfolio.specialty}</Text>
            </View>
          )}
        </View>
      )}

      {/* Stats */}
      <View style={styles.portfolioStats}>
        <View style={styles.statBox}>
          <Text style={styles.statValue}>{portfolio.completed_count ?? 0}</Text>
          <Text style={styles.statLabel}>{t('portfolio.stat_completed')}</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statValue}>{portfolio.completion_rate ?? 0}%</Text>
          <Text style={styles.statLabel}>{t('portfolio.stat_success')}</Text>
        </View>
        {portfolio.avg_rating != null && (
          <View style={styles.statBox}>
            <Text style={styles.statValue}>{portfolio.avg_rating.toFixed(1)} ★</Text>
            <Text style={styles.statLabel}>{t('portfolio.stat_rating')}</Text>
          </View>
        )}
      </View>

      {/* Agreements list */}
      {(portfolio.agreements ?? []).length > 0 && (
        <>
          <Text style={styles.portfolioSectionLabel}>{t('portfolio.completedAgreements')}</Text>
          {(portfolio.agreements ?? []).map((item) => (
            <View key={item.id} style={styles.agreementCard}>
              <View style={styles.agreementMain}>
                <Ionicons name="checkmark-circle" size={16} color={colors.success} />
                <Text style={styles.agreementTitle} numberOfLines={2}>{item.title ?? ''}</Text>
              </View>
              <View style={styles.agreementMeta}>
                <Text style={styles.agreementDate}>{item.completed_at ? formatDate(item.completed_at) : '—'}</Text>
                {item.rating != null && (
                  <Text style={styles.agreementRating}>
                    {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                  </Text>
                )}
              </View>
            </View>
          ))}
        </>
      )}
    </View>
  );
}

// ─── ConnectionBlock ──────────────────────────────────────────────────────────

type ConnectionBlockProps = {
  status: ConnectionStatus;
  busy: boolean;
  onSend: () => void;
  onAccept: () => void;
  onDecline: () => void;
  onRemove: () => void;
};

function ConnectionBlock({ status, busy, onSend, onAccept, onDecline, onRemove }: ConnectionBlockProps) {
  const { colors } = useTheme();
  const connStyles = useMemo(() => makeConnStyles(colors), [colors]);
  const { t } = useTranslation();

  if (status === 'accepted') {
    return (
      <View style={connStyles.row}>
        <View style={connStyles.acceptedChip}>
          <Ionicons name="people" size={15} color={colors.success} />
          <Text style={connStyles.acceptedText}>{t('connection.inCircle')}</Text>
        </View>
        <Pressable onPress={onRemove} disabled={busy} hitSlop={8} style={connStyles.removeBtn}>
          <Text style={connStyles.removeText}>{t('connection.action_remove')}</Text>
        </Pressable>
      </View>
    );
  }

  if (status === 'pending_received') {
    return (
      <View style={connStyles.respondRow}>
        <Text style={connStyles.respondLabel}>{t('connection.addedYou')}</Text>
        <View style={connStyles.respondButtons}>
          <Button title={t('common.accept')} icon="checkmark" onPress={onAccept} loading={busy} style={connStyles.respondBtn} />
          <Button title={t('common.cancel')} variant="secondary" onPress={onDecline} loading={busy} style={connStyles.respondBtn} />
        </View>
      </View>
    );
  }

  if (status === 'pending_sent') {
    return (
      <View style={connStyles.pendingChip}>
        <Ionicons name="time-outline" size={15} color={colors.textMuted} />
        <Text style={connStyles.pendingText}>{t('connection.pending')}</Text>
      </View>
    );
  }

  // 'none' | 'cooldown' — στο cooldown το κουμπί είναι disabled χωρίς εξήγηση
  return (
    <Button
      title={t('connection.action_add')}
      icon="person-add-outline"
      variant="secondary"
      onPress={onSend}
      loading={busy}
      disabled={status === 'cooldown'}
    />
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function makeConnStyles(colors: AppColors) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.sm,
    },
    acceptedChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: `${colors.success}18`,
      borderRadius: radius.full,
      paddingVertical: 8,
      paddingHorizontal: spacing.md,
      flex: 1,
    },
    acceptedText: {
      color: colors.success,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    removeBtn: {
      paddingVertical: 8,
      paddingHorizontal: spacing.sm,
    },
    removeText: {
      color: colors.danger,
      fontSize: fontSize.sm,
      fontWeight: '500',
    },
    respondRow: {
      gap: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.lg,
      padding: spacing.md,
      backgroundColor: colors.surfaceAlt,
    },
    respondLabel: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '500',
      textAlign: 'center',
    },
    respondButtons: {
      flexDirection: 'row',
      gap: spacing.sm,
    },
    respondBtn: {
      flex: 1,
    },
    pendingChip: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 10,
      paddingHorizontal: spacing.md,
      backgroundColor: colors.surfaceAlt,
    },
    pendingText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '500',
    },
  });
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    loader: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background,
    },
    content: {
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    topBar: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    back: {
      color: colors.primaryLight,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    header: { gap: spacing.xs, alignItems: 'center', paddingHorizontal: spacing.md },
    avatarWrap: {
      alignItems: 'center',
      marginBottom: 14,
    },
    badgesOverlay: {
      position: 'absolute',
      bottom: -14,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      justifyContent: 'center',
    },
    name: {
      color: colors.text,
      fontSize: fontSize.xxl,
      fontWeight: '700',
      textAlign: 'center',
      marginTop: 2,
    },
    nickname: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontStyle: 'italic',
      textAlign: 'center',
      marginTop: 1,
    },
    businessName: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
      textAlign: 'center',
      marginTop: 2,
      letterSpacing: 0.2,
    },
    bio: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 22,
      textAlign: 'center',
      marginTop: spacing.xs,
    },
    disputeFlagPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      paddingHorizontal: spacing.sm,
      paddingVertical: 4,
      marginTop: spacing.xs,
      alignSelf: 'center',
    },
    disputeFlagText: {
      color: colors.textMuted,
      fontSize: 11,
      fontWeight: '500',
    },
    infoStrip: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: spacing.sm,
      marginTop: spacing.xs,
    },
    infoItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    infoText: {
      color: colors.textMuted,
      fontSize: 12,
    },
    error: {
      color: colors.danger,
      fontSize: fontSize.sm,
      textAlign: 'center',
    },
    portfolioCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.md,
      ...shadow.card,
    },
    portfolioHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    portfolioTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },
    portfolioTitle: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    businessLogoSection: {
      alignItems: 'center',
      gap: spacing.md,
      marginVertical: spacing.sm,
    },
    businessLogo: {
      width: '100%',
      aspectRatio: 1,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    specialtyBadge: {
      alignSelf: 'center',
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      paddingHorizontal: spacing.md,
      paddingVertical: 5,
      maxWidth: '100%',
    },
    specialtyText: {
      color: colors.primary,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    portfolioStats: {
      flexDirection: 'row',
      gap: spacing.sm,
    },
    statBox: {
      flex: 1,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      padding: spacing.sm,
      alignItems: 'center',
      gap: 2,
    },
    statValue: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '800',
    },
    statLabel: {
      color: colors.textMuted,
      fontSize: 10,
      textAlign: 'center',
      lineHeight: 13,
    },
    portfolioSectionLabel: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    agreementCard: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      padding: spacing.sm,
      gap: 4,
    },
    agreementMain: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.xs,
    },
    agreementTitle: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    agreementMeta: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingLeft: 24,
    },
    agreementDate: {
      color: colors.textMuted,
      fontSize: 12,
    },
    agreementRating: {
      color: colors.primary,
      fontSize: 12,
      letterSpacing: 1,
    },
    announcementCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: 'rgba(59,130,246,0.3)',
      padding: spacing.md,
      gap: spacing.sm,
    },
    announcementHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },
    announcementLabel: {
      color: '#3B82F6',
      fontSize: fontSize.sm,
      fontWeight: '700',
      flex: 1,
    },
    announcementExpiry: {
      color: colors.textMuted,
      fontSize: 11,
    },
    announcementText: {
      color: colors.text,
      fontSize: fontSize.md,
      lineHeight: 22,
    },
    announcementImage: {
      width: '100%',
      height: 140,
      borderRadius: radius.md,
    },
  });
}

type AnnouncementCardProps = {
  announcement: import('@/lib/announcements').Announcement;
  colors: AppColors;
  styles: ReturnType<typeof makeStyles>;
};

function AnnouncementCard({ announcement, colors, styles }: AnnouncementCardProps) {
  const { t } = useTranslation();
  const msLeft = new Date(announcement.expires_at).getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));

  return (
    <View style={styles.announcementCard}>
      <View style={styles.announcementHeader}>
        <Ionicons name="megaphone-outline" size={16} color="#3B82F6" />
        <Text style={styles.announcementLabel}>{t('user.announcement')}</Text>
        <Text style={styles.announcementExpiry}>{t(daysLeft === 1 ? 'user.expiresIn_one' : 'user.expiresIn_other', { count: daysLeft })}</Text>
      </View>
      <Text style={styles.announcementText}>{announcement.text}</Text>
      {!!announcement.image_url && (
        <ExpoImage source={{ uri: announcement.image_url }} style={styles.announcementImage} contentFit="cover" cachePolicy="disk" />
      )}
    </View>
  );
}
